/**
 * Wizard step 3 — income & value. Monthly rent and rehab cost. Both feed
 * `property_setting` (monthly_rent / repair_cost); everything else is refined
 * later on the property detail page.
 */
import type { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import type { AddPropertyFormValues } from "@/components/properties/add-property/schema";

interface StepIncomeProps {
  form: UseFormReturn<AddPropertyFormValues>;
}

export function StepIncome({ form }: StepIncomeProps) {
  const isFlip = form.watch("strategy") === "Flip";

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField
        control={form.control}
        name="monthlyRent"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Monthly rent</FormLabel>
            <FormControl>
              <MoneyInput placeholder="1,650" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormDescription>
              {isFlip
                ? "Optional — a flip carries vacant until sold."
                : "Projected rent; actual collected rent overrides it later."}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="rehabCost"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Rehab cost</FormLabel>
            <FormControl>
              <MoneyInput placeholder="Optional" value={field.value} onChange={field.onChange} />
            </FormControl>
            <FormDescription>Leave blank if none.</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Flip-only exit assumption — drives `flip_selling_cost` and the
          profit/margin tiles, which read as a loss when it is missing. */}
      {isFlip && (
        <FormField
          control={form.control}
          name="sellPct"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Estimated selling cost (%)</FormLabel>
              <FormControl>
                <NumberInput
                  step="0.25"
                  placeholder="7"
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormDescription>
                Percent of ARV — agent commission, transfer tax, sell-side closing.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}

function MoneyInput({
  value,
  onChange,
  placeholder,
}: {
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
        $
      </span>
      <NumberInput
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="pl-7"
      />
    </div>
  );
}
